import { UserMfaType } from 'features/profile/enums/user-mfa-type-enum';

export type MfaChallenge = {
  mfaId: string;
  mfaType: number;
};

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null;

const readMfaId = (body: Record<string, unknown>): string | null => {
  const mfaId = body.mfa_id ?? body.mfaId;
  return typeof mfaId === 'string' && mfaId ? mfaId : null;
};

const readMfaType = (body: Record<string, unknown>): number => {
  const mfaTypeRaw = body.mfa_type ?? body.mfaType;
  const mfaType =
    typeof mfaTypeRaw === 'number'
      ? mfaTypeRaw
      : typeof mfaTypeRaw === 'string' && mfaTypeRaw
        ? Number(mfaTypeRaw)
        : UserMfaType.EMAIL_VERIFICATION;

  return Number.isFinite(mfaType) ? mfaType : UserMfaType.EMAIL_VERIFICATION;
};

const isMfaRequired = (body: Record<string, unknown>): boolean =>
  body.error === 'mfa_enabled' ||
  body.mfa_required === true ||
  body.enable_mfa === true ||
  body.mfaRequired === true;

export const getMfaChallengeFromLoginResult = (result: unknown): MfaChallenge | null => {
  if (!isRecord(result)) {
    return null;
  }

  const mfaId = readMfaId(result);
  if (!mfaId || !isMfaRequired(result)) {
    return null;
  }

  return {
    mfaId,
    mfaType: readMfaType(result),
  };
};

export const getMfaChallengeFromError = (error: unknown): MfaChallenge | null => {
  if (!isRecord(error)) {
    return null;
  }

  if (isRecord(error.error)) {
    const fromNested = getMfaChallengeFromLoginResult(error.error);
    if (fromNested) {
      return fromNested;
    }
  }

  if (isRecord(error.response)) {
    const fromAxios = getMfaChallengeFromLoginResult(error.response.data);
    if (fromAxios) {
      return fromAxios;
    }
  }

  return getMfaChallengeFromLoginResult(error);
};

export const buildVerifyKeyPath = (mfaId: string, mfaType: number, username: string) =>
  `/verify-key?mfa_id=${encodeURIComponent(mfaId)}&mfa_type=${mfaType}&user_name=${encodeURIComponent(username.toLowerCase())}`;
